import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { formatMoneyCHF } from '../utils/priceUtils';
import {
  FaSearch, FaTimes, FaCheck, FaPercent, FaTag, FaBuilding,
  FaCalendarAlt, FaSpinner, FaPlus, FaTrash, FaInfoCircle, FaBolt,
} from 'react-icons/fa';
import {
  searchProducts,
  searchCompanies,
  fetchProductsByCompany,
  updateProductDiscount,
} from '../services/apiService';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Bulk Discount Modal — admin discount manager
 *
 * Workflow:
 *   1. Find products via free-text search and/or a company filter
 *   2. Multi-select the products to discount
 *   3. Configure ONE discount rule (kind + amount + optional schedule + label)
 *   4. Submit — applies the same rule to every selected pharmacode
 *
 * Backend support: `PUT /api/<csv-pharmacodes>/discount` already accepts
 * comma-separated pharmacode lists, so the submit is a single request.
 */
const BulkDiscountModal = ({ isOpen, onClose, onSuccess }) => {
  const { t } = useLanguage();

  // ── Selection panel state ──────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [companyQuery, setCompanyQuery] = useState('');
  const [companies, setCompanies] = useState([]);
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Map<pharmacode, { pharmacode, name, basePrice, company }>
  const [selected, setSelected] = useState(new Map());

  // ── Rule form state ────────────────────────────────────────────────
  const [kind, setKind] = useState('percent');
  const [amount, setAmount] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [endsAt, setEndsAt] = useState('');
  const [label, setLabel] = useState('');

  // ── Submission state ───────────────────────────────────────────────
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submitResult, setSubmitResult] = useState(null);

  // Reset everything when the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    setSearchTerm('');
    setCompanyFilter('');
    setCompanyQuery('');
    setSearchResults([]);
    setSelected(new Map());
    setKind('percent');
    setAmount('');
    setStartsAt('');
    setEndsAt('');
    setLabel('');
    setError(null);
    setSubmitResult(null);
  }, [isOpen]);

  // Company typeahead — searches as the admin types.
  useEffect(() => {
    if (!isOpen) return;
    if (!companyQuery.trim() || companyQuery.trim().length < 2) {
      setCompanies([]);
      return;
    }
    const timer = setTimeout(async () => {
      try {
        const r = await searchCompanies(companyQuery.trim(), 10);
        const list = r?.data?.companies || r?.companies || [];
        setCompanies(list);
      } catch {
        setCompanies([]);
      }
    }, 200);
    return () => clearTimeout(timer);
  }, [companyQuery, isOpen]);

  // Run search whenever the term or company filter changes (debounced).
  useEffect(() => {
    if (!isOpen) return;
    const term = searchTerm.trim();
    const hasFilters = term.length >= 2 || !!companyFilter;
    if (!hasFilters) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    const timer = setTimeout(async () => {
      try {
        let response;
        if (companyFilter) {
          response = await fetchProductsByCompany(companyFilter, 1, 50);
        } else {
          response = await searchProducts(term, 1, 50);
        }
        const products = response?.data?.products || [];
        setSearchResults(products);
      } catch (e) {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, companyFilter, isOpen]);

  // Normalize a result product into a row: the first displayable article.
  // Dedup by pharmacode defensively — the search endpoint already collapses
  // cross-hit duplicates, but the HCI catalog can split one article across
  // multiple parent docs, so keeping a Map here makes the modal robust
  // against any future regression in the backend. First occurrence wins
  // (highest-scoring hit per ES ordering).
  const rowsFromResults = useMemo(() => {
    const byCode = new Map();
    for (const p of searchResults) {
      const article = p.articles?.[0];
      if (!article || !article.pharmacode) continue;
      if (byCode.has(article.pharmacode)) continue;
      const desc =
        article.description?.longDescription ||
        article.description?.description ||
        article.longDescription ||
        `Pharmacode ${article.pharmacode}`;
      const company = Array.isArray(p.companies) && p.companies[0]?.name
        ? p.companies[0].name
        : null;
      const basePrice =
        article.price?.basePrice ??
        article.basePrice ??
        article.publicPrice?.price ??
        null;
      const currentDiscount = article.discount || null;
      byCode.set(article.pharmacode, {
        pharmacode: article.pharmacode,
        name: desc,
        company,
        basePrice,
        currentDiscount,
        hasPhoto: !!article.hasPhoto,
      });
    }
    return Array.from(byCode.values());
  }, [searchResults]);

  const toggleSelect = useCallback((row) => {
    setSelected((prev) => {
      const next = new Map(prev);
      if (next.has(row.pharmacode)) next.delete(row.pharmacode);
      else next.set(row.pharmacode, row);
      return next;
    });
  }, []);

  const removeSelected = useCallback((pharmacode) => {
    setSelected((prev) => {
      const next = new Map(prev);
      next.delete(pharmacode);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rowsFromResults) if (!next.has(r.pharmacode)) next.set(r.pharmacode, r);
      return next;
    });
  }, [rowsFromResults]);

  const deselectAllVisible = useCallback(() => {
    setSelected((prev) => {
      const next = new Map(prev);
      for (const r of rowsFromResults) next.delete(r.pharmacode);
      return next;
    });
  }, [rowsFromResults]);

  // Live preview of what the discount will do across the selection.
  const previewStats = useMemo(() => {
    const items = Array.from(selected.values());
    const withBase = items.filter((i) => Number.isFinite(Number(i.basePrice)));
    const totalBase = withBase.reduce((sum, i) => sum + Number(i.basePrice), 0);
    const amt = parseFloat(amount);
    let totalAfter = totalBase;
    let savings = 0;
    if (Number.isFinite(amt) && amt > 0) {
      if (kind === 'percent' && amt < 100) {
        totalAfter = totalBase * (1 - amt / 100);
        savings = totalBase - totalAfter;
      } else if (kind === 'fixed') {
        totalAfter = withBase.length * amt;
        savings = totalBase - totalAfter;
      }
    }
    return {
      count: items.length,
      pricedCount: withBase.length,
      totalBase,
      totalAfter,
      savings,
    };
  }, [selected, amount, kind]);

  const handleSubmit = async () => {
    setError(null);
    const codes = Array.from(selected.keys());
    if (codes.length === 0) {
      setError('Select at least one product to apply a discount.');
      return;
    }
    const numAmount = parseFloat(amount);
    if (!Number.isFinite(numAmount) || numAmount <= 0) {
      setError('Enter a valid discount amount.');
      return;
    }
    if (kind === 'percent' && numAmount >= 100) {
      setError('Percentage must be less than 100.');
      return;
    }
    if (startsAt && endsAt && new Date(startsAt) >= new Date(endsAt)) {
      setError('End date must be after start date.');
      return;
    }

    setIsSubmitting(true);
    try {
      const rule = {
        kind,
        amount: numAmount,
        startsAt: startsAt ? new Date(startsAt).toISOString() : undefined,
        endsAt: endsAt ? new Date(endsAt).toISOString() : undefined,
        label: label || undefined,
      };
      const pharmacodesCsv = codes.join(',');
      const response = await updateProductDiscount(pharmacodesCsv, rule);
      setSubmitResult(response);
      if (response.success) {
        onSuccess?.(response);
      } else {
        setError(response.error || response.message || 'Failed to apply discounts.');
      }
    } catch (e) {
      const data = e.response?.data;
      setError(data?.error || data?.message || e.message || 'Request failed.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      >
        <motion.div
          className="w-full max-w-6xl max-h-[92vh] flex flex-col bg-white rounded-2xl shadow-2xl overflow-hidden"
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          transition={{ duration: 0.18 }}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-5 border-b border-gray-200 bg-gradient-to-r from-red-50 to-pink-50">
            <div className="flex items-center gap-3">
              <div className="flex items-center justify-center w-11 h-11 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 text-white shadow-md">
                <FaBolt className="text-xl" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Bulk discount</h2>
                <p className="text-sm text-gray-600">
                  Select multiple products and apply the same discount rule to all of them at once.
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors"
              aria-label="Close"
            >
              <FaTimes />
            </button>
          </div>

          {/* Body: two columns */}
          <div className="flex-1 grid grid-cols-1 lg:grid-cols-5 gap-0 overflow-hidden">
            {/* ── Left: search + results ─────────────────────────── */}
            <div className="lg:col-span-3 flex flex-col border-r border-gray-200 min-h-0">
              <div className="p-5 space-y-3 bg-gray-50/60 border-b border-gray-200">
                {/* Search bar */}
                <div className="relative">
                  <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Search by name, brand, pharmacode…"
                    className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  />
                </div>

                {/* Company filter */}
                <div className="relative">
                  <FaBuilding className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <input
                    type="text"
                    value={companyFilter ? companyFilter : companyQuery}
                    onChange={(e) => {
                      setCompanyQuery(e.target.value);
                      setCompanyFilter('');
                      setShowCompanyDropdown(true);
                    }}
                    onFocus={() => setShowCompanyDropdown(true)}
                    onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                    placeholder="Filter by company / brand (optional)"
                    className="w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  />
                  {(companyFilter || companyQuery) && (
                    <button
                      type="button"
                      onClick={() => {
                        setCompanyFilter('');
                        setCompanyQuery('');
                        setCompanies([]);
                      }}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                      aria-label="Clear company filter"
                    >
                      <FaTimes />
                    </button>
                  )}
                  {showCompanyDropdown && companies.length > 0 && !companyFilter && (
                    <div className="absolute z-20 top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg max-h-60 overflow-auto">
                      {companies.map((c, i) => {
                        const name = typeof c === 'string' ? c : (c.name || c.companyName || '');
                        if (!name) return null;
                        return (
                          <button
                            key={`${name}-${i}`}
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => {
                              setCompanyFilter(name);
                              setCompanyQuery('');
                              setShowCompanyDropdown(false);
                            }}
                            className="w-full text-left px-4 py-2 text-sm hover:bg-gray-100"
                          >
                            {name}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Active filter chips */}
                {companyFilter && (
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">
                      <FaBuilding className="w-3 h-3" />
                      {companyFilter}
                      <button
                        onClick={() => setCompanyFilter('')}
                        className="ml-1 hover:text-indigo-900"
                        aria-label="Remove company filter"
                      >
                        <FaTimes className="w-2.5 h-2.5" />
                      </button>
                    </span>
                  </div>
                )}
              </div>

              {/* Results header with bulk-select controls */}
              <div className="flex items-center justify-between px-5 py-2 bg-white border-b border-gray-100 text-xs text-gray-500">
                <span>
                  {isSearching ? (
                    <span className="inline-flex items-center gap-1.5">
                      <FaSpinner className="animate-spin" /> Searching…
                    </span>
                  ) : rowsFromResults.length > 0 ? (
                    `${rowsFromResults.length} result${rowsFromResults.length === 1 ? '' : 's'}`
                  ) : (
                    'No results yet'
                  )}
                </span>
                {rowsFromResults.length > 0 && (
                  <div className="flex items-center gap-3">
                    <button
                      type="button"
                      onClick={selectAllVisible}
                      className="text-red-600 hover:text-red-700 font-medium"
                    >
                      Select all visible
                    </button>
                    <span className="text-gray-300">·</span>
                    <button
                      type="button"
                      onClick={deselectAllVisible}
                      className="text-gray-600 hover:text-gray-800 font-medium"
                    >
                      Deselect visible
                    </button>
                  </div>
                )}
              </div>

              {/* Scrollable results list */}
              <div className="flex-1 overflow-y-auto min-h-0">
                {rowsFromResults.length === 0 && !isSearching && (
                  <div className="p-12 text-center text-gray-500">
                    <FaSearch className="w-8 h-8 mx-auto mb-3 text-gray-300" />
                    <p className="font-medium">Start typing or pick a company</p>
                    <p className="text-sm mt-1">Results will appear here. Click a row to add it.</p>
                  </div>
                )}
                {rowsFromResults.map((row) => {
                  const isSel = selected.has(row.pharmacode);
                  const isPriceless = row.basePrice == null;
                  return (
                    <button
                      key={row.pharmacode}
                      type="button"
                      onClick={() => { if (!isPriceless) toggleSelect(row); }}
                      disabled={isPriceless}
                      title={
                        isPriceless
                          ? 'This product has no base price in the imported data, so it cannot be discounted. Update the public price on the product first.'
                          : undefined
                      }
                      className={`w-full text-left flex items-center gap-3 px-5 py-3 border-b border-gray-100 transition-colors ${
                        isPriceless
                          ? 'opacity-60 cursor-not-allowed'
                          : isSel
                            ? 'bg-red-50/70 hover:bg-red-50'
                            : 'hover:bg-gray-50'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 transition-colors ${
                          isPriceless
                            ? 'border-gray-200 bg-gray-100'
                            : isSel
                              ? 'bg-red-500 border-red-500 text-white'
                              : 'border-gray-300 bg-white'
                        }`}
                      >
                        {isSel && !isPriceless && <FaCheck className="w-3 h-3" />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-gray-900 truncate">{row.name}</div>
                        <div className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                          <span>#{row.pharmacode}</span>
                          {row.company && <><span>·</span><span className="truncate">{row.company}</span></>}
                          {row.currentDiscount && (
                            <>
                              <span>·</span>
                              <span className="text-amber-600 font-medium">already on discount</span>
                            </>
                          )}
                        </div>
                      </div>
                      {isPriceless ? (
                        <div className="text-right text-xs italic text-gray-400 whitespace-nowrap">
                          No base price
                        </div>
                      ) : (
                        <div className="text-right text-sm text-gray-700 font-medium tabular-nums whitespace-nowrap">
                          {formatMoneyCHF(row.basePrice)}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* ── Right: selection summary + rule form ────────── */}
            <div className="lg:col-span-2 flex flex-col bg-gradient-to-b from-white to-gray-50 min-h-0">
              {/* Selected list */}
              <div className="px-5 py-4 border-b border-gray-200 flex-shrink-0">
                <div className="flex items-baseline justify-between mb-2">
                  <h3 className="font-semibold text-gray-900">
                    Selected <span className="text-red-600">({selected.size})</span>
                  </h3>
                  {selected.size > 0 && (
                    <button
                      type="button"
                      onClick={() => setSelected(new Map())}
                      className="text-xs text-gray-500 hover:text-gray-700"
                    >
                      Clear all
                    </button>
                  )}
                </div>
                <div className="max-h-40 overflow-y-auto -mx-1">
                  {selected.size === 0 ? (
                    <p className="text-sm text-gray-400 italic px-1 py-2">
                      No products selected yet.
                    </p>
                  ) : (
                    Array.from(selected.values()).map((row) => (
                      <div
                        key={row.pharmacode}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-white text-sm"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="truncate text-gray-800">{row.name}</div>
                          <div className="text-xs text-gray-500">
                            #{row.pharmacode}
                            {row.basePrice != null && <> · {formatMoneyCHF(row.basePrice)}</>}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSelected(row.pharmacode)}
                          className="text-gray-400 hover:text-red-600 p-1"
                          aria-label="Remove"
                        >
                          <FaTrash className="w-3 h-3" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Rule form */}
              <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    Discount type
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setKind('percent')}
                      className={`px-3 py-2.5 rounded-xl border font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                        kind === 'percent'
                          ? 'bg-red-500 text-white border-red-500 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <FaPercent className="w-3 h-3" />
                      Percentage
                    </button>
                    <button
                      type="button"
                      onClick={() => setKind('fixed')}
                      className={`px-3 py-2.5 rounded-xl border font-medium text-sm transition-all flex items-center justify-center gap-2 ${
                        kind === 'fixed'
                          ? 'bg-red-500 text-white border-red-500 shadow-md'
                          : 'bg-white text-gray-700 border-gray-300 hover:border-gray-400'
                      }`}
                    >
                      <FaTag className="w-3 h-3" />
                      Fixed CHF
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    {kind === 'percent' ? 'Percentage off' : 'Final price (CHF)'}
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      step={kind === 'percent' ? '1' : '0.01'}
                      min="0"
                      max={kind === 'percent' ? '99' : undefined}
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      placeholder={kind === 'percent' ? 'e.g. 20' : 'e.g. 8.50'}
                      className="w-full px-4 py-2.5 pr-14 border border-gray-300 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium text-sm">
                      {kind === 'percent' ? '%' : 'CHF'}
                    </span>
                  </div>
                  {kind === 'fixed' && (
                    <p className="mt-1.5 text-xs text-amber-700 flex items-start gap-1.5">
                      <FaInfoCircle className="w-3 h-3 mt-0.5 flex-shrink-0" />
                      Fixed price applies the same final CHF to every selected product, regardless of their base prices.
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2 flex items-center gap-1.5">
                    <FaCalendarAlt className="w-3 h-3" />
                    Schedule (optional)
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <p className="text-[10px] text-gray-500 mb-1">Starts</p>
                      <input
                        type="datetime-local"
                        value={startsAt}
                        onChange={(e) => setStartsAt(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      />
                    </div>
                    <div>
                      <p className="text-[10px] text-gray-500 mb-1">Ends</p>
                      <input
                        type="datetime-local"
                        value={endsAt}
                        onChange={(e) => setEndsAt(e.target.value)}
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">
                    Campaign label (optional)
                  </label>
                  <input
                    type="text"
                    value={label}
                    onChange={(e) => setLabel(e.target.value)}
                    placeholder="e.g. Spring Sale 2026"
                    className="w-full px-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 bg-white"
                  />
                </div>

                {/* Live preview */}
                {selected.size > 0 && (
                  <div className="bg-gradient-to-br from-gray-50 to-white border border-gray-200 rounded-xl p-3 space-y-1.5 text-sm">
                    <div className="font-semibold text-gray-700 text-xs uppercase tracking-wide mb-1">
                      Preview
                    </div>
                    <div className="flex justify-between text-gray-600">
                      <span>Products</span>
                      <span className="font-medium text-gray-900">{previewStats.count}</span>
                    </div>
                    {previewStats.pricedCount > 0 && (
                      <>
                        <div className="flex justify-between text-gray-600">
                          <span>Combined base</span>
                          <span className="font-medium text-gray-900 tabular-nums">
                            {formatMoneyCHF(previewStats.totalBase)}
                          </span>
                        </div>
                        {amount && Number.isFinite(parseFloat(amount)) && (
                          <>
                            <div className="flex justify-between text-gray-600">
                              <span>After discount</span>
                              <span className="font-medium text-red-600 tabular-nums">
                                {formatMoneyCHF(previewStats.totalAfter)}
                              </span>
                            </div>
                            <div className="flex justify-between border-t border-gray-200 pt-1.5">
                              <span className="text-green-700 font-medium">Customers save</span>
                              <span className="font-bold text-green-700 tabular-nums">
                                {formatMoneyCHF(previewStats.savings)}
                              </span>
                            </div>
                          </>
                        )}
                      </>
                    )}
                    {previewStats.count > previewStats.pricedCount && (
                      <p className="text-[11px] text-amber-700 mt-1 flex items-start gap-1">
                        <FaInfoCircle className="w-2.5 h-2.5 mt-0.5 flex-shrink-0" />
                        {previewStats.count - previewStats.pricedCount} selected product
                        {previewStats.count - previewStats.pricedCount === 1 ? '' : 's'} ha
                        {previewStats.count - previewStats.pricedCount === 1 ? 's' : 've'} no base price and may be rejected by the server.
                      </p>
                    )}
                  </div>
                )}

                {error && (
                  <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded-lg text-sm">
                    {error}
                  </div>
                )}

                {submitResult?.success && (
                  <div className="bg-green-50 border border-green-200 text-green-800 px-3 py-2 rounded-lg text-sm">
                    {submitResult.message ||
                      `Applied to ${submitResult.data?.summary?.successCount ?? selected.size} product(s).`}
                    {submitResult.data?.summary?.failedCount > 0 && (
                      <div className="mt-1 text-xs">
                        {submitResult.data.summary.failedCount} failed — see network response for details.
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50 flex-shrink-0">
            <div className="text-sm text-gray-600">
              {selected.size === 0
                ? 'No products selected'
                : `${selected.size} product${selected.size === 1 ? '' : 's'} selected`}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isSubmitting}
                className="px-5 py-2.5 rounded-xl border border-gray-300 bg-white text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {submitResult?.success ? 'Close' : 'Cancel'}
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || selected.size === 0 || !amount}
                className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-red-500 to-pink-600 text-white font-semibold shadow-md hover:from-red-600 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    Applying…
                  </>
                ) : (
                  <>
                    <FaPlus />
                    Apply discount to {selected.size || 0}
                  </>
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default BulkDiscountModal;
